import { defineStore, storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useUserStore } from '@/stores/user'
import { FiltersService } from '@/services/telehealthApi/FiltersService'
import {
  arrayVitalSignsFilterKeys,
  type ArrayVitalSignsFilterKeys,
  booleanVitalSignsFilterKeys,
  type BooleanVitalSignsFilterKeys,
  type VitalSignFilters
} from '@/stores/models/VitalSignsFilter'
import {
  AllCaseOrderOptionsDTO,
  type CasesWithVitalSignsFilterDTOHeartRateRange,
  CasesWithVitalSignsFilterDTOSortDirectionEnum,
  type CasesWithVitalSignsFilterOptionsResponse,
  type CaseWithVitalSignsDTO,
  type DepartmentDTO,
  type ErrorResponseDto,
  type GetVitalSignsResponse,
  type HospitalDTO,
  type PredictionResultDTO,
  type VitalSignItemDTO,
  type WardDTO
} from '@/generated'
import { remove } from 'lodash'
import type { VitalSignsSortBy } from './models/VitalSignsSortBy'
import { NoticeType } from './models/Notice'
import { useNoticeStore } from './notice'
import { DisplayMode, type VitalSignsDisplayMode } from './models/VitalSignsDisplayMode'
import { formatVitalSigns } from '@/util/formatVitalSigns'
import { formatLaboratoryData } from '@/util/formatLaboratoryData'
import { CaseService } from '@/services/telehealthApi/CaseService'
import { LaboratoryService } from '@/services/telehealthApi/LaboratoryService'
import { VitalSignsService } from '@/services/telehealthApi/VitalSignsService'
import { OrganSupportService } from '@/services/telehealthApi/OrganSupportService'
import { OutputService } from '@/services/telehealthApi/OutputService'
import { useI18n } from 'vue-i18n'
import { EdsService } from '@/services/telehealthApi/EdsService'
import { AkiPredictionsService } from '@/services/telehealthApi/AkiPredictionsService'
import { useEdsPlugin } from '@/composables/useEdsPlugin'
import { useAkiPlugin } from '@/composables/useAkiPlugin'
import { usePluginsStore } from '@/stores/plugins'
import { withMinimumDuration } from '@/util/withMinimumDuration'

export type { VitalSignsSortBy }

const MINIMUM_LOADING_DURATION_MS = 300

export type CaseWithVitalSigns = CaseWithVitalSignsDTO & {
  akiScore?: PredictionResultDTO | ErrorResponseDto | undefined
}

function toRangeFilter(
  range: readonly (string | number)[] | undefined
): CasesWithVitalSignsFilterDTOHeartRateRange | undefined {
  if (!range || range.length !== 2) return undefined
  return { min: Number(range[0]), max: Number(range[1]) }
}

const CASES_LIMIT = 75

const defaultVitalSignsFilter: VitalSignFilters = {
  temperatureRange: [],
  lactateRange: [],
  pHRange: [],
  potassiumRange: [],
  hemoglobinRange: [],
  heartRateRange: [],
  oxygenSaturationRange: [],
  vasopressors: undefined,
  dialysis: undefined,
  ventilation: undefined,
  ecmo: undefined,
  impella: undefined,
  hospitals: [],
  departments: [],
  wards: []
}

const defaultVitalSignsDisplayMode: VitalSignsDisplayMode = {
  display: DisplayMode.COMPACT
}

const defaultVitalSignsSortBy: VitalSignsSortBy = {
  sortBy: AllCaseOrderOptionsDTO.PATIENT_NAME,
  sortDirection: CasesWithVitalSignsFilterDTOSortDirectionEnum.ASCENDING
}

export const usePatientsWithVitalSignsStore = defineStore('patientsWithVitalSigns', () => {
  const { t } = useI18n()
  const noticeStore = useNoticeStore()
  const userStore = useUserStore()
  const { hasAcceptedEdsLimitation } = useEdsPlugin()
  const { hasAcceptedAkiLimitation } = useAkiPlugin()
  const { isEdsEnabled, isAkiEnabled } = storeToRefs(usePluginsStore())

  const vitalSignsLoading = ref(false)
  const moreCasesLoading = ref(false)
  const filteringInProgress = ref(false)
  const casesWithVitalSigns = ref<CaseWithVitalSigns[]>([])
  const caseWithVitalSigns = ref<CaseWithVitalSigns | null>(null)

  const currentPage = ref(1)
  const itemsPerPage = ref(10)

  const vitalSignsFilters = ref<VitalSignFilters>({ ...defaultVitalSignsFilter })
  const vitalSignsSortBy = ref<VitalSignsSortBy>({ ...defaultVitalSignsSortBy })
  const vitalSignsDisplayMode = ref<VitalSignsDisplayMode>({ ...defaultVitalSignsDisplayMode })

  const filterOptions = ref<CasesWithVitalSignsFilterOptionsResponse>({
    hospitals: [],
    departments: [],
    wards: []
  })

  async function fetchFilterOptions() {
    try {
      filterOptions.value = await CaseService.getCasesWithVitalSignsFilterOptions()
    } catch {
      noticeStore.add({ type: NoticeType.ERROR, message: 'Failed to load filter options' })
    }
  }

  async function commitVitalSigns() {
    filteringInProgress.value = true
    currentPage.value = 1

    const payload = {
      ...vitalSignsFilters.value,
      ...vitalSignsSortBy.value,
      ...vitalSignsDisplayMode.value
    }

    await FiltersService.postVitalSignsFilters(userStore.user?.userName || '', {
      vitalSigns: [payload]
    })

    await getCasesWithVitalSigns()
    filteringInProgress.value = false
  }

  async function initVitalSignsDisplayOptions() {
    const username = userStore.user?.userName
    if (!username) return

    const response = await FiltersService.getVitalSignsFilters(username)
    const vitalSigns = response?.vitalSigns || []

    if (!vitalSigns.length) return

    const filterData = vitalSigns[0] as Record<string, any>

    vitalSignsFilters.value = { ...defaultVitalSignsFilter, ...filterData }

    vitalSignsFilters.value.departments = (vitalSignsFilters.value.departments ?? []).filter(
      (d): d is DepartmentDTO => typeof d === 'object' && d !== null && 'id' in d
    )
    vitalSignsFilters.value.wards = (vitalSignsFilters.value.wards ?? []).filter(
      (w): w is WardDTO => typeof w === 'object' && w !== null && 'id' in w
    )

    vitalSignsSortBy.value = filterData.sortBy
      ? {
          sortBy: filterData.sortBy as AllCaseOrderOptionsDTO,
          sortDirection: filterData.sortDirection as CasesWithVitalSignsFilterDTOSortDirectionEnum
        }
      : { ...defaultVitalSignsSortBy }

    vitalSignsDisplayMode.value = filterData.display
      ? { display: filterData.display }
      : { ...defaultVitalSignsDisplayMode }
  }

  const totalCases = ref(0)
  const currentLimit = ref(CASES_LIMIT)

  const hasMoreCases = computed<boolean>(() => {
    return totalCases.value > casesWithVitalSigns.value.length
  })

  async function getCasesWithVitalSigns() {
    try {
      vitalSignsLoading.value = true

      const filters = vitalSignsFilters.value
      const response = await withMinimumDuration(
        CaseService.getCasesWithVitalSigns({
          limit: itemsPerPage.value,
          offset: (currentPage.value - 1) * itemsPerPage.value,
          sortBy: vitalSignsSortBy.value.sortBy,
          sortDirection: vitalSignsSortBy.value.sortDirection,
          ecmo: filters.ecmo,
          impella: filters.impella,
          dialysis: filters.dialysis,
          ventilation: filters.ventilation,
          vasopressors: filters.vasopressors,
          hospitalIds: filters.hospitals?.map((h) => h.id),
          departmentIds: filters.departments?.map((d) => d.id),
          wardIds: filters.wards?.map((w) => w.id),
          temperatureRange: toRangeFilter(filters.temperatureRange),
          heartRateRange: toRangeFilter(filters.heartRateRange),
          oxygenSaturationRange: toRangeFilter(filters.oxygenSaturationRange),
          lactateRange: toRangeFilter(filters.lactateRange),
          phRange: toRangeFilter(filters.pHRange),
          potassiumRange: toRangeFilter(filters.potassiumRange),
          hemoglobinRange: toRangeFilter(filters.hemoglobinRange)
        }),
        MINIMUM_LOADING_DURATION_MS
      )

      casesWithVitalSigns.value = response.items.map((item) => ({
        ...item,
        vitalSigns: formatVitalSigns(item.vitalSigns),
        laboratory: formatLaboratoryData(item.laboratory)
      }))
      totalCases.value = response.totalResults
    } catch {
      casesWithVitalSigns.value = []
      noticeStore.add({ type: NoticeType.ERROR, message: 'Failed to load vital signs' })
    } finally {
      vitalSignsLoading.value = false
    }
  }

  async function getCaseWithVitalSigns(caseId: string, wardId: string, shareCode?: string) {
    caseWithVitalSigns.value = null

    try {
      const edsScorePromise =
        isEdsEnabled.value && hasAcceptedEdsLimitation.value
          ? EdsService.getClinicalScore(caseId, wardId).catch((error) => {
              console.error('Failed to load EDS score for single case:', error)
              return null
            })
          : Promise.resolve(null)

      const akiScorePromise: Promise<PredictionResultDTO | ErrorResponseDto | undefined> =
        isAkiEnabled.value && hasAcceptedAkiLimitation.value
          ? AkiPredictionsService.getAkiInfoPanel(caseId)
              .then((panel) => panel.predictionResult)
              .catch((error) => {
                console.error('Failed to load AKI score for single case:', error)
                return (error?.response?.data as ErrorResponseDto) || undefined
              })
          : Promise.resolve(undefined)

      const [caseDetails, laboratoryVitalSigns, vitalSigns, organSupport, edsScore, akiScore] =
        await Promise.all([
          CaseService.getCase(caseId, shareCode),
          LaboratoryService.getLatestLaboratoryValues(caseId, shareCode),
          VitalSignsService.getLatestVitalSigns(caseId, shareCode),
          OrganSupportService.getOrganSupport(caseId, shareCode),
          edsScorePromise,
          akiScorePromise
        ])

      caseWithVitalSigns.value = {
        case: caseDetails.case,
        laboratory: (laboratoryVitalSigns.items ?? []).map((item) => item.vitalSigns[0]),
        vitalSigns: (vitalSigns.items ?? []).map((item) => item.vitalSigns[0]),
        organSupport: organSupport,
        ...(edsScore && { edsScore }),
        ...(akiScore && { akiScore })
      }
    } catch {
      caseWithVitalSigns.value = null
      noticeStore.add({
        type: NoticeType.ERROR,
        message: 'Failed to load vital signs'
      })
    }
  }

  function removeHospitalFilter(hospitalId: string) {
    remove(vitalSignsFilters.value.hospitals, (h: HospitalDTO) => h.id === hospitalId)

    cleanupVitalSignsFilter(vitalSignsFilters.value)
    commitVitalSigns()
  }

  function removeDepartmentFilter(departmentId: string) {
    remove(vitalSignsFilters.value.departments, (d) => d.id === departmentId)
    commitVitalSigns()
  }

  function removeWardFilter(wardId: string) {
    remove(vitalSignsFilters.value.wards, (w) => w.id === wardId)
    commitVitalSigns()
  }

  function isArrayVitalSignsFilterKey(key: any): key is ArrayVitalSignsFilterKeys {
    return arrayVitalSignsFilterKeys.includes(key)
  }

  function isBooleanVitalSignsFilterKey(key: any): key is BooleanVitalSignsFilterKeys {
    return booleanVitalSignsFilterKeys.includes(key)
  }

  function removeRangeFilter(key: ArrayVitalSignsFilterKeys | BooleanVitalSignsFilterKeys) {
    if (isArrayVitalSignsFilterKey(key)) {
      vitalSignsFilters.value[key] = []
    } else if (isBooleanVitalSignsFilterKey(key)) {
      vitalSignsFilters.value[key] = undefined
    }

    commitVitalSigns()
  }

  function cleanupVitalSignsFilter(filter: VitalSignFilters) {
    if (!filter.hospitals.length) return

    const hospitalIds = new Set(filter.hospitals.map((h) => h.id))
    filter.departments = filter.departments.filter((d) => hospitalIds.has(d.hospitalId))

    const departmentIdsUnderSelectedHospitals = new Set(
      filterOptions.value.departments
        .filter((o) => hospitalIds.has(o.department.hospitalId))
        .map((o) => o.department.id)
    )
    filter.wards = filter.wards.filter((w) =>
      departmentIdsUnderSelectedHospitals.has(w.departmentId)
    )
  }

  async function loadMoreCasesWithVitalSigns() {
    if (!hasMoreCases.value || moreCasesLoading.value || vitalSignsLoading.value) {
      return
    }

    currentLimit.value += CASES_LIMIT
    moreCasesLoading.value = true
    await getCasesWithVitalSigns()
    moreCasesLoading.value = false
  }

  function setLabValuesForCase(
    caseId: string,
    labValues: VitalSignItemDTO[]
  ): CaseWithVitalSignsDTO[] {
    return casesWithVitalSigns.value.map((caseWithVitalSigns) => {
      if (caseWithVitalSigns.case.id === caseId) {
        return {
          ...caseWithVitalSigns,
          laboratory: [...caseWithVitalSigns.laboratory, ...labValues]
        }
      }
      return caseWithVitalSigns
    })
  }

  async function getLabValues(
    caseId: string,
    callback: (caseId: string, shareCode: string | undefined) => Promise<GetVitalSignsResponse>,
    errorMessage: string
  ): Promise<void> {
    try {
      const response = await callback(caseId, undefined)
      const latestLabValues = response.items.map((item) => item.vitalSigns[0])

      casesWithVitalSigns.value = setLabValuesForCase(caseId, formatLaboratoryData(latestLabValues))
    } catch (error) {
      casesWithVitalSigns.value = setLabValuesForCase(caseId, [])
      noticeStore.add({ type: NoticeType.ERROR, message: errorMessage })
    }
  }

  async function getPatientLabValues(caseId: string): Promise<void> {
    await getLabValues(
      caseId,
      LaboratoryService.getLatestLaboratoryValues,
      t('Failed to load laboratory values')
    )

    await getLabValues(
      caseId,
      OutputService.getLatestOutputVitalSigns,
      t('Failed to load output values')
    )
  }

  return {
    vitalSignsFilters,
    filterOptions,
    fetchFilterOptions,
    commitVitalSigns,
    initVitalSignsDisplayOptions,
    removeHospitalFilter,
    removeDepartmentFilter,
    removeWardFilter,
    vitalSignsLoading,
    moreCasesLoading,
    getCasesWithVitalSigns,
    loadMoreCasesWithVitalSigns,
    casesWithVitalSigns,
    caseWithVitalSigns,
    vitalSignsSortBy,
    removeRangeFilter,
    vitalSignsDisplayMode,
    hasMoreCases,
    totalCases,
    filteringInProgress,
    currentPage,
    itemsPerPage,
    getPatientLabValues,
    getCaseWithVitalSigns
  }
})
